import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  LinearProgress,
} from '@mui/material'
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Calculate as CalculateIcon,
  People as PeopleIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ro } from 'date-fns/locale'
import { useAuth } from '../contexts/AuthContext'
import apiClient from '../services/api'
import { useRecomputeProgress, formatProgressMessage } from '../hooks/useRecomputeProgress'

interface MedicScore {
  doctor_id: string
  doctor_name: string
  score: number
  studies_count: number
  urgencies_done: number
  avg_pickup_sec: number
  avg_finalize_sec: number
  completion_rate: number
  urgency_rate: number
  stars: number
  score_percentage: number
}

interface LeaderboardData {
  entries: MedicScore[]
  total: number
  period_start: string
  period_end: string
  avg_score: number
  top_score: number
  bottom_score: number
}

interface RecomputeData {
  message: string
  recomputed_scores: number
  period_start: string
  period_end: string
  computed_at: string
  avg_score_before: number
  avg_score_after: number
  score_changes: Record<string, number>
}

const ScoringPage: React.FC = () => {
  const { user } = useAuth()
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [recomputing, setRecomputing] = useState(false)
  const [recomputeDialog, setRecomputeDialog] = useState(false)
  const [recomputeData, setRecomputeData] = useState<RecomputeData | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  
  // Hook pentru progress tracking
  const { progress, error: progressError } = useRecomputeProgress(jobId)
  const [institutions, setInstitutions] = useState<Array<{id: string, name: string}>>([])
  const [selectedMedic, setSelectedMedic] = useState<MedicScore | null>(null)
  const [medicDetailsDialog, setMedicDetailsDialog] = useState(false)
  
  // Filters
  const [fromDate, setFromDate] = useState<Date | null>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  const [toDate, setToDate] = useState<Date | null>(new Date())
  const [institutionFilter, setInstitutionFilter] = useState<string>('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    fetchLeaderboard()
    // Doar admin și operator pot accesa instituțiile
    if (user?.role !== 'medic') {
      fetchInstitutions()
    }
  }, [fromDate, toDate, institutionFilter, user?.role])

  const fetchInstitutions = async () => {
    try {
      const response = await apiClient.get('/scoring/institutions')
      setInstitutions(response.data || [])
    } catch (err) {
      console.error('Error fetching institutions:', err)
    }
  }

  const fetchLeaderboard = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams()
      if (fromDate) params.append('from', fromDate.toISOString().split('T')[0])
      if (toDate) params.append('to', toDate.toISOString().split('T')[0])
      if (institutionFilter) params.append('institution_id', institutionFilter)

      // Pentru medic, încarcă doar scorul lui
      let endpoint = '/scoring/leaderboard';
      if (user?.role === 'medic') {
        endpoint = `/scoring/doctor/${user.id}`;
      }

      const response = await apiClient.get(`${endpoint}?${params.toString()}`)
      
      if (user?.role === 'medic') {
        // Pentru medic, creează un format compatibil cu leaderboard
        const medicData = response.data;
        setLeaderboardData({
          entries: [{
            doctor_id: medicData.doctor_id || user.id,
            doctor_name: medicData.doctor_name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            score: medicData.score || 0,
            studies_count: medicData.studies_count || 0,
            urgencies_done: medicData.urgent_studies || 0,
            avg_pickup_sec: medicData.avg_pickup_time || 0,
            avg_finalize_sec: medicData.avg_finalize_time || 0,
            completion_rate: medicData.completion_rate || 0,
            urgency_rate: medicData.urgency_rate || 0,
            stars: medicData.stars || 0,
            score_percentage: medicData.score_percentage || 0
          }],
          total: 1,
          period_start: medicData.period?.from || fromDate?.toISOString().split('T')[0] || '',
          period_end: medicData.period?.to || toDate?.toISOString().split('T')[0] || '',
          avg_score: medicData.score || 0,
          top_score: medicData.score || 0,
          bottom_score: medicData.score || 0
        });
      } else {
        setLeaderboardData(response.data);
      }

    } catch (err: any) {
      console.error('Error fetching leaderboard:', err)
      setError(err.response?.data?.detail || 'Eroare la încărcarea leaderboard-ului')
    } finally {
      setLoading(false)
    }
  }

  const handleViewMedicDetails = (doctorId: string) => {
    const medic = leaderboardData?.entries.find(entry => entry.doctor_id === doctorId)
    if (medic) {
      setSelectedMedic(medic)
      setMedicDetailsDialog(true)
    }
  }

  const handleRecompute = async () => {
    try {
      setRecomputing(true)
      setError(null)
      setJobId(null) // Reset job ID

      const params = new URLSearchParams()
      if (fromDate) params.append('from', fromDate.toISOString().split('T')[0])
      if (toDate) params.append('to', toDate.toISOString().split('T')[0])

      const response = await apiClient.post(`/scoring/recompute?${params.toString()}`)
      
      // Extract job_id from response details
      if (response.data.details && response.data.details.job_id) {
        setJobId(response.data.details.job_id)
      }
      
      setRecomputeData(response.data)
      setRecomputeDialog(true)

      // Refresh leaderboard after recompute
      await fetchLeaderboard()

    } catch (err: any) {
      console.error('Error recomputing scores:', err)
      setError(err.response?.data?.detail || 'Eroare la recalcularea scorurilor')
    } finally {
      setRecomputing(false)
      setJobId(null) // Clear job ID când se termină
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 4.0) return 'success'
    if (score >= 3.0) return 'warning'
    return 'error'
  }



  const formatTime = (seconds: number) => {
    if (!seconds) return 'N/A'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  // Compact styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }

  return (
    <Box p={1.5}>

      {/* Filters - Compact */}
      <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.paper' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <DatePicker label="De la" value={fromDate} onChange={(newValue) => setFromDate(newValue)} slotProps={{ textField: { size: 'small', sx: { width: 140, ...inputSx } } }} />
            <DatePicker label="Până la" value={toDate} onChange={(newValue) => setToDate(newValue)} slotProps={{ textField: { size: 'small', sx: { width: 140, ...inputSx } } }} />
            {user?.role !== 'medic' && (
              <FormControl size="small" sx={{ minWidth: 150, ...inputSx }}>
                <InputLabel>Instituție</InputLabel>
                <Select value={institutionFilter} onChange={(e) => setInstitutionFilter(e.target.value)} label="Instituție">
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
                  {institutions.map((institution) => (<MenuItem key={institution.id} value={institution.id} sx={{ fontSize: '0.8rem' }}>{institution.name}</MenuItem>))}
                </Select>
              </FormControl>
            )}
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchLeaderboard} size="small" sx={{ height: 32, fontSize: '0.75rem', textTransform: 'none' }}>Actualizează</Button>
          </Box>
        </LocalizationProvider>
      </Paper>

      {/* Summary Cards - Compact */}
      {leaderboardData && (
        <Grid container spacing={1.5} mb={1.5}>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  <AssessmentIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Scor Mediu</Typography>
                </Box>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'primary.main' }}>{leaderboardData.avg_score.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Din {leaderboardData.total} medici</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  <TrendingUpIcon color="success" sx={{ mr: 0.5, fontSize: 18 }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Cel Mai Bun</Typography>
                </Box>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'success.main' }}>{leaderboardData.top_score.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Scor maxim</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  <TrendingDownIcon color="error" sx={{ mr: 0.5, fontSize: 18 }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Cel Mai Slab</Typography>
                </Box>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'error.main' }}>{leaderboardData.bottom_score.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Scor minim</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  <CalculateIcon color="info" sx={{ mr: 0.5, fontSize: 18 }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Recalculează</Typography>
                </Box>
                <Button variant="contained" onClick={handleRecompute} disabled={recomputing} fullWidth startIcon={recomputing ? <CircularProgress size={14} /> : <CalculateIcon sx={{ fontSize: 14 }} />} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
                  {recomputing ? 'Recalc...' : 'Recalculează'}
                </Button>
                {recomputing && progress && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress variant="determinate" value={progress.percentage} sx={{ height: 4, borderRadius: 1 }} />
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.25 }}>{formatProgressMessage(progress)}</Typography>
                  </Box>
                )}
                {progressError && <Alert severity="warning" sx={{ mt: 1, py: 0.25, fontSize: '0.7rem' }}>{progressError}</Alert>}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Leaderboard Table - Compact */}
      {leaderboardData && (
        <Paper sx={{ 
          bgcolor: 'background.paper',
          '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
          '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
          '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
        }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                  <TableCell>#</TableCell>
                  <TableCell>Medic</TableCell>
                  <TableCell align="center">Scor</TableCell>
                  <TableCell align="center">Stele</TableCell>
                  <TableCell align="center">Studii</TableCell>
                  <TableCell align="center">Urgențe</TableCell>
                  <TableCell align="center">Pickup</TableCell>
                  <TableCell align="center">Finalize</TableCell>
                  <TableCell align="center">Compl.</TableCell>
                  <TableCell align="center">Urg.R.</TableCell>
                  <TableCell align="center">{user?.role !== 'medic' ? 'Act.' : 'St.'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboardData.entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((medic, index) => (
                    <TableRow key={medic.doctor_id} hover>
                      <TableCell><Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'primary.main' }}>#{page * rowsPerPage + index + 1}</Typography></TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{medic.doctor_name}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{medic.doctor_id.slice(0, 8)}...</Typography>
                      </TableCell>
                      <TableCell align="center"><Chip label={medic.score.toFixed(2)} color={getScoreColor(medic.score) as any} variant="outlined" size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} /></TableCell>
                      <TableCell align="center">
                        <Rating value={medic.stars} readOnly size="small" sx={{ '& .MuiRating-icon': { fontSize: 12 } }} />
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{medic.score_percentage.toFixed(0)}%</Typography>
                      </TableCell>
                      <TableCell align="center"><Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{medic.studies_count}</Typography></TableCell>
                      <TableCell align="center"><Chip label={medic.urgencies_done} size="small" color={medic.urgencies_done > 0 ? "success" : "warning"} variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                      <TableCell align="center"><Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{formatTime(medic.avg_pickup_sec)}</Typography></TableCell>
                      <TableCell align="center"><Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{formatTime(medic.avg_finalize_sec)}</Typography></TableCell>
                      <TableCell align="center"><Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{formatPercentage(medic.completion_rate)}</Typography></TableCell>
                      <TableCell align="center"><Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{formatPercentage(medic.urgency_rate)}</Typography></TableCell>
                      <TableCell align="center">
                        {user?.role !== 'medic' && (<Tooltip title="Detalii"><IconButton size="small" color="primary" onClick={() => handleViewMedicDetails(medic.doctor_id)}><VisibilityIcon /></IconButton></Tooltip>)}
                        {user?.role === 'medic' && (<Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Scor</Typography>)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={leaderboardData.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            labelRowsPerPage="Rânduri:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} din ${count}`}
            sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
          />
        </Paper>
      )}

      {/* Recompute Dialog */}
      <Dialog 
        open={recomputeDialog} 
        onClose={() => setRecomputeDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <Box display="flex" alignItems="center">
            <CalculateIcon sx={{ mr: 1 }} />
            Recalculare Scoruri
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'background.paper' }}>
          {recomputeData && (
            <Box>
              <Typography variant="body1" gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                {recomputeData.message}
              </Typography>
              
              <Grid container spacing={2} mt={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom>
                      Scoruri Recalculate
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {recomputeData.recomputed_scores}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom>
                      Perioada
                    </Typography>
                    <Typography variant="body1">
                      {new Date(recomputeData.period_start).toLocaleDateString('ro-RO')} - {new Date(recomputeData.period_end).toLocaleDateString('ro-RO')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Box mt={3}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                  Comparație Scoruri
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body2" sx={{ minWidth: 120 }}>
                        Scor mediu înainte:
                      </Typography>
                      <Chip
                        label={recomputeData.avg_score_before?.toFixed(2) || 'N/A'}
                        color="warning"
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body2" sx={{ minWidth: 120 }}>
                        Scor mediu după:
                      </Typography>
                      <Chip
                        label={recomputeData.avg_score_after?.toFixed(2) || 'N/A'}
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {Object.keys(recomputeData.score_changes).length > 0 && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                    Schimbări Individuale
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>Medic ID</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary' }}>Schimbare Scor</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(recomputeData.score_changes).map(([doctorId, change]) => (
                          <TableRow key={doctorId} hover>
                            <TableCell sx={{ color: 'text.primary' }}>{doctorId}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={change > 0 ? `+${change.toFixed(2)}` : change.toFixed(2)}
                                color={change > 0 ? 'success' : change < 0 ? 'error' : 'default'}
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
          <Button 
            onClick={() => setRecomputeDialog(false)}
            variant="contained"
            color="primary"
          >
            Închide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refresh Info */}
      <Box mt={4} display="flex" justifyContent="center">
        <Typography variant="body2" color="text.secondary">
          Ultima actualizare: {new Date().toLocaleString('ro-RO')}
        </Typography>
      </Box>

      {/* Medic Details Dialog */}
      <Dialog 
        open={medicDetailsDialog} 
        onClose={() => setMedicDetailsDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <Box display="flex" alignItems="center">
            <PeopleIcon sx={{ mr: 1 }} />
            Detalii Medic
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'background.paper' }}>
          {selectedMedic && (
            <Box>
              <Grid container spacing={3}>
                {/* Informații de bază */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                      Informații Medic
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
                      <strong>Nume:</strong> {selectedMedic.doctor_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>ID:</strong> {selectedMedic.doctor_id}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Scor:</strong> {selectedMedic.score.toFixed(2)} / 5.0
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Stele:</strong> {selectedMedic.stars}/5 ({selectedMedic.score_percentage.toFixed(1)}%)
                    </Typography>
                  </Paper>
                </Grid>

                {/* Metrici de performanță */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                      Metrici Performanță
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Studii:</strong> {selectedMedic.studies_count}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Urgențe:</strong> {selectedMedic.urgencies_done}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Completion Rate:</strong> {formatPercentage(selectedMedic.completion_rate)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Urgency Rate:</strong> {formatPercentage(selectedMedic.urgency_rate)}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Timpi de răspuns */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                      Timpi de Răspuns
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Pickup Time:</strong> {formatTime(selectedMedic.avg_pickup_sec)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Finalize Time:</strong> {formatTime(selectedMedic.avg_finalize_sec)}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Perioada de analiză */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                      Perioada Analiză
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>De la:</strong> {fromDate?.toLocaleDateString('ro-RO')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      <strong>Până la:</strong> {toDate?.toLocaleDateString('ro-RO')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
          <Button 
            onClick={() => setMedicDetailsDialog(false)}
            variant="contained"
            color="primary"
          >
            Închide
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ScoringPage
